import type {
  AccessoryProductType,
  AccessoryWithQuantityType
} from 'data/types';
import type {
  ManualAccessoryLogActionType,
  SavedQuotableSystemAccessoryProductType
} from 'features/api/accessories-api-types';
import type {
  AccessoryOwnerType,
  ManualAccessoryEntryType,
  ManualAccessorySourceType
} from 'utils/manual-accessories-types';

export const MANUAL_ACCESSORY_TYPE = 'manual';

export const ACCESSORY_SEARCH_MIN_TERM_LENGTH = 3;

export const isSearchableManualAccessoryTerm = (term: string): boolean =>
  term.trim().length >= ACCESSORY_SEARCH_MIN_TERM_LENGTH;

export const toManualAccessoryProduct = (
  product: SavedQuotableSystemAccessoryProductType
): AccessoryProductType => ({
  productId: product.productId,
  name: product.name,
  description: product.description,
  price: product.price,
  type: MANUAL_ACCESSORY_TYPE,
  productUrl: product.productUrl,
  photo: product.photo,
  availableStock: product.availableStock
});

export const buildSavedRowProductLookup = (
  rows: ManualAccessorySourceType[]
): Map<number, AccessoryProductType> =>
  rows.reduce((lookup, row) => {
    if (row.product && !lookup.has(row.productId)) {
      lookup.set(row.productId, toManualAccessoryProduct(row.product));
    }
    return lookup;
  }, new Map<number, AccessoryProductType>());

const isSelected = (accessory: AccessoryWithQuantityType): boolean =>
  accessory.quantity > 0;

export const isManualAccessory = (
  accessory: AccessoryWithQuantityType
): boolean => accessory.type === MANUAL_ACCESSORY_TYPE;

export const condenserAccessoryEntries = (
  condensers: AccessoryOwnerType[]
): ManualAccessoryEntryType[] =>
  condensers.flatMap((condenser) =>
    condenser.selectedAccessories.map((accessory) => ({
      ...accessory,
      ownerUniqueId: condenser.uniqueId
    }))
  );

export const findManualAccessories = <T extends AccessoryWithQuantityType>(
  accessories: T[]
): T[] =>
  accessories.filter(
    (accessory) => isSelected(accessory) && isManualAccessory(accessory)
  );

export const selectedAccessoryProductIds = (
  accessories: AccessoryWithQuantityType[]
): Set<number> =>
  new Set(
    accessories.filter(isSelected).map((accessory) => accessory.productId)
  );

export const manualAccessoryKey = (entry: ManualAccessoryEntryType): string =>
  `${entry.ownerUniqueId ?? 'system'}:${entry.productId}`;

export const resolveManualAccessoryLogAction = (
  current: ManualAccessoryEntryType[],
  productId: number,
  quantity: number,
  ownerUniqueId?: string
): ManualAccessoryLogActionType | null => {
  const selected = current.filter(
    (entry) => entry.productId === productId && isSelected(entry)
  );
  if (quantity > 0) return selected.length === 0 ? 'added' : null;
  if (selected.length === 0) return null;
  const selectedElsewhere = selected.some(
    (entry) => entry.ownerUniqueId !== ownerUniqueId
  );
  return selectedElsewhere ? null : 'removed';
};

export const sortManualAccessoriesByName = <T extends { name: string }>(
  accessories: T[]
): T[] =>
  accessories
    .slice()
    .sort((left, right) => left.name.localeCompare(right.name));
